import { CENTS_PER_KM_CAP_KM, centsPerKmRate } from "@stb/jurisdiction-au"
import {
  Car,
  FileSpreadsheet,
  Gauge,
  MapPinned,
  Pencil,
  Route,
  Smartphone,
  Timer,
} from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"

import { PhoneTripMock, TripIllustration } from "../../../components/home/home-art"
import { displayYear } from "../../../components/home/home-content"
import { LogbookIllustration, MethodsMock } from "../../../components/landing/product-art"
import { type ProductPageContent, withIcons } from "../../../components/landing/product-page"
import { MILEAGE_EXAMPLE as ex } from "../../products/example-numbers"
import { MILEAGE_TRACKER } from "../../products/mileage-tracker"
import { englishDateToZh, formatDateZh } from "../dates"

/** Chinese counterpart of products/mileage-tracker.tsx; terms: docs/design/landing/copy-zh.md. */
const rate = centsPerKmRate(ex.financialYear)
const year = displayYear(ex.financialYear)
const checked = formatDateZh(rate.checkedOn)
const cap = CENTS_PER_KM_CAP_KM.toLocaleString("en-AU")

export const MILEAGE_SEO_ZH = {
  title: "澳洲个体户行车日志与里程记录应用",
  description:
    "手机 GPS 自动记录行程，每趟写明用途；并排比较 ATO 的两种方法，导出 CSV 行程日志。仅为估算，可免费开始。",
}

const COPY: ProductPageContent = {
  path: "/mileage-tracker",
  name: "里程记录",
  tag: "里程记录",
  reviewed: englishDateToZh(MILEAGE_TRACKER.reviewed),
  h1: "帮你把行车日志做好的里程记录应用",
  lead: "手机用 GPS 记录工作行程，你几秒钟补上用途。报税时并排比较 ATO 的两种方法，再下载行程日志。",
  answer: {
    text: `申报工作用车的扣除额有两种方法：cents per km 法（每辆车每年最多 ${cap} 个业务公里），或 logbook 法，用 12 周的行车日志算出实际车辆开支里业务用途所占的份额。${year} 财年的 cents per km 费率是 ${rate.cents} 分。`,
    sources: [
      { label: "ATO，cents per km 法（个人）", href: rate.sourceUrl },
      {
        label: "企业页面",
        href: "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/income-and-deductions-for-business/deductions/deductions-for-motor-vehicle-expenses/cents-per-kilometre-method",
      },
    ],
    financialYear: year,
    checked,
  },
  cta: { label: "开始免费试用", href: "/login" },
  secondaryCta: { label: "查看价格", href: "/pricing" },
  heroNote: "支持 iPhone 和 Android。扣除额仅为估算。",
  stats: [
    { value: `${rate.cents} 分`, label: `${year} 财年每公里` },
    { value: `${cap} km`, label: "cents per km 法每辆车上限" },
    { value: "CSV", label: "下载行程日志" },
    { value: "2 种方法", label: "并排比较" },
  ],
  stepsTitle: "从开车到出报告，三步",
  steps: [
    { title: "开车", body: "行程自动开始和结束，先存在手机上，联网后再同步。" },
    { title: "复核", body: "在网页上为每趟行程写明用途，标记是业务还是私人。" },
    { title: "比较并下载", body: "并排看两种方法，再把行程日志下载成 CSV 交给会计师。" },
  ],
  example: {
    title: "两种方法比较",
    caption: "两种方法比较：示例年度",
    columns: ["示例年度", "cents per km 法", "Logbook 法"],
    rows: [
      { header: "业务公里数", cells: [ex.businessKm, ex.businessKm] },
      { header: "估算扣除额", cells: [ex.centsPerKm, ex.logbook] },
    ],
    note: `仅为示例：${ex.businessKm} 个业务公里，业务用途占 ${ex.businessPercent}%，车辆开支 ${ex.carCosts}。在业务用途 ${ex.businessPercent}%、${ex.businessKm} 公里的情况下，全年车辆开支超过约 ${ex.breakEven} 后，logbook 法的扣除额更高。仅为估算。`,
  },
  included: [
    { title: "自动记录行程", body: "手机识别驾车，用 GPS 记录路线并保存行程。" },
    {
      title: "需要时再补用途",
      body: "几秒钟写明用途，标记业务或私人；在网页上可以一次处理多趟。",
    },
    {
      title: "两种方法比较",
      body: "给出 cents per km 法与 logbook 法的估算，以及两者持平的临界点。",
    },
    { title: "里程表读数", body: "为每辆车记录里程表读数，和行程放在一起。" },
    { title: "CSV 下载", body: "把行程日志下载成 CSV，或把全部数据导出成 zip。" },
  ],
  limits: {
    title: "手机才是记录设备",
    body: (
      <>
        网站无法在后台追踪行程：用应用记录，用网站复核和出报告。Kestly
        提供的是估算，不是税务建议。摩托车，以及载重 1 吨或以上、或可载 9
        人或以上的车辆，不能使用这两种方法。来源：{" "}
        <a
          href={rate.sourceUrl}
          className="focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
        >
          ATO，自有或租赁汽车的开支
        </a>
        ，核对于 {checked}。
      </>
    ),
  },
  who: [
    {
      title: "个体经营者和承包商",
      body: "在各个工地之间开车、申报车辆开支的水电工、保洁和顾问。",
    },
    {
      title: "申报车辆开支的雇员",
      body: "里程记录方案不需要 ABN。把行程和用途集中放在一处。",
    },
    { title: "网约车和外卖司机", body: "把业务行程和私人驾驶分开记录。" },
  ],
  trust: {
    title: "你的行程有据可查",
    body: "行程先存在手机上，联网后同步。需要时随时把行程日志下载成 CSV。数据存放在哪里，见安全与隐私页面。",
  },
  plan: {
    rows: ["trips", "vehicles"],
    extraRows: [{ header: "两种方法比较", cells: ["有", "有", "有"] }],
  },
  faqTitle: "里程记录常见问题",
  faq: [
    {
      question: "我需要 logbook 吗？",
      answer: (
        <>
          cents per km 法不需要，每辆车最多可算 {cap} 个业务公里。logbook 法需要连续至少 12
          周的行车日志。来源：{" "}
          <a
            href={rate.sourceUrl}
            className="focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
          >
            ATO，cents per km 法
          </a>
          ，{year} 财年，核对于 {checked}。
        </>
      ),
    },
    {
      question: "没有 ABN 可以用吗？",
      answer: "可以。里程记录方案不含发票和账目，所以不会要求你填 ABN。",
    },
    {
      question: "没有信号能用吗？",
      answer: "能。行程存在手机上，联网后自动同步。",
    },
  ],
  related: [
    { label: "Logbook 法什么时候更划算", href: "/logbook-method" },
    { label: "cents per km 费率与计算器", href: "/cents-per-km-calculator" },
    { label: "收据和开支", href: "/receipt-scanner" },
    { label: "开发票", href: "/invoicing" },
    { label: "网约车和外卖司机：ABN、GST、BAS", href: "/rideshare-drivers" },
    { label: "Kestly 对比 Driversnote", href: "/compare/driversnote-alternative" },
    { label: "电子表格还是应用？", href: "/compare/spreadsheet-logbook" },
    { label: "使用方法", href: "/how-it-works" },
    { label: "价格", href: "/pricing" },
    { label: "数据存放在哪里", href: "/trust" },
  ],
  band: {
    title: "今天就开始你的里程日志",
    description: `选择“只记录我的驾驶”，Logbook 套餐免费试用 ${TRIAL_DAYS} 天，无需绑定银行卡。`,
  },
}

const STEP_ART = [
  <TripIllustration key="trip" />,
  <LogbookIllustration key="logbook" />,
  <MethodsMock key="methods" />,
]

/** Icons and pictures are presentation only (same as the English page). */
export const MILEAGE_TRACKER_ZH: ProductPageContent = {
  ...COPY,
  heroCard: <PhoneTripMock />,
  stats: withIcons(COPY.stats, [Gauge, Car, Timer, FileSpreadsheet]),
  steps: COPY.steps.map((step, i) => ({ ...step, art: STEP_ART[i], artOnMobile: i === 2 })),
  included: withIcons(COPY.included, [Route, Pencil, Gauge, MapPinned, FileSpreadsheet]),
  limits: { ...COPY.limits, icon: Smartphone },
  who: withIcons(COPY.who, [Car, Pencil, Route]),
}
